import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ArrowRight, ArrowLeft, Sparkles, Users, Target, Trophy } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";

interface OnboardingModalProps {
  isOpen: boolean;
  onClose: () => void;
}

const steps = [
  {
    icon: Sparkles,
    title: "Welcome to The Prompt Syndicate",
    description: "The premier network for AI creators and prompt engineers. Let's show you around.",
    gradient: "from-[#47A6FF] to-[#9B4DFF]",
  },
  {
    icon: Target,
    title: "GEO Optimization",
    description: "Your prompts are ranked by quality and originality using our GEO (Generative Engine Optimization) system. Create high-performance prompts to climb the rankings.",
    gradient: "from-[#9B4DFF] to-[#FF3FB3]",
  },
  {
    icon: Users,
    title: "The Creator Network",
    description: "Join live channels to discuss prompts, AI tools, and workflows. Collaborate with creators across dedicated channels for prompt design, AI engineering, and creative strategy.",
    gradient: "from-[#FF3FB3] to-[#47A6FF]",
  },
  {
    icon: Trophy,
    title: "Build Your Reputation",
    description: "Early adopters receive recognition badges. Share prompts, engage with the community, and establish yourself as a trusted creator in the AI ecosystem.",
    gradient: "from-[#FFD35A] to-[#FFBB00]",
  },
];

export function OnboardingModal({ isOpen, onClose }: OnboardingModalProps) {
  const [currentStep, setCurrentStep] = useState(0);
  const step = steps[currentStep];
  const Icon = step.icon;

  const handleNext = () => {
    if (currentStep < steps.length - 1) {
      setCurrentStep(currentStep + 1);
    } else {
      onClose();
    }
  };

  const handlePrev = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleSkip = () => {
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden border-border/50">
        <button
          onClick={handleSkip}
          className="absolute right-4 top-4 rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:pointer-events-none z-50"
        >
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>

        <div className="relative">
          {/* Gradient header */}
          <div className={`h-32 bg-gradient-to-r ${step.gradient} flex items-center justify-center`}>
            <Icon className="h-16 w-16 text-white" />
          </div>

          {/* Content */}
          <div className="p-8 pb-6">
            <AnimatePresence mode="wait">
              <motion.div
                key={currentStep}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, y: -10 }}
                transition={{ duration: 0.3 }}
              >
                <DialogHeader className="mb-6">
                  <DialogTitle className="text-2xl font-bold text-center mb-3">
                    {step.title}
                  </DialogTitle>
                  <p className="text-muted-foreground text-center leading-relaxed">
                    {step.description}
                  </p>
                </DialogHeader>

                {/* Progress indicators */}
                <div className="flex justify-center gap-2 mb-6">
                  {steps.map((_, index) => (
                    <div
                      key={index}
                      className={`h-2 rounded-full transition-all ${
                        index === currentStep
                          ? "w-8 bg-primary"
                          : index < currentStep
                          ? "w-2 bg-primary/60"
                          : "w-2 bg-muted"
                      }`}
                    />
                  ))}
                </div>

                {/* Navigation buttons */}
                <div className="flex items-center justify-between gap-3">
                  <Button
                    variant="ghost"
                    onClick={handlePrev}
                    disabled={currentStep === 0}
                    className="flex items-center gap-2"
                  >
                    <ArrowLeft className="h-4 w-4" />
                    Back
                  </Button>

                  <Button
                    variant="outline"
                    onClick={handleSkip}
                    className="text-muted-foreground"
                  >
                    Skip
                  </Button>

                  <Button
                    onClick={handleNext}
                    className="flex items-center gap-2 bg-gradient-to-r from-primary to-primary/80"
                  >
                    {currentStep === steps.length - 1 ? "Get Started" : "Next"}
                    {currentStep < steps.length - 1 && <ArrowRight className="h-4 w-4" />}
                  </Button>
                </div>
              </motion.div>
            </AnimatePresence>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
